// Keyboard shortcuts manager

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  description: string;
  handler: () => void;
}

class KeyboardManager {
  private shortcuts: Map<string, KeyboardShortcut> = new Map();
  private enabled = true;

  constructor() {
    this.setupListener();
  }

  private setupListener(): void {
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (!this.enabled) return;

      // Don't trigger shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const key = e.key.toLowerCase();
      const shortcutKey = this.getShortcutKey(key, e.ctrlKey || e.metaKey, e.shiftKey, e.altKey);
      const shortcut = this.shortcuts.get(shortcutKey);

      if (shortcut) {
        e.preventDefault();
        e.stopPropagation();
        shortcut.handler();
      }
    });
  }

  private getShortcutKey(
    key: string,
    ctrl: boolean,
    shift: boolean,
    alt: boolean
  ): string {
    const parts: string[] = [];
    if (ctrl) parts.push('ctrl');
    if (shift) parts.push('shift');
    if (alt) parts.push('alt');
    parts.push(key);
    return parts.join('+');
  }

  register(shortcut: KeyboardShortcut): void {
    const key = this.getShortcutKey(
      shortcut.key.toLowerCase(),
      shortcut.ctrl || false,
      shortcut.shift || false,
      shortcut.alt || false
    );
    this.shortcuts.set(key, shortcut);
  }

  unregister(key: string, ctrl = false, shift = false, alt = false): void {
    const shortcutKey = this.getShortcutKey(key.toLowerCase(), ctrl, shift, alt);
    this.shortcuts.delete(shortcutKey);
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
  }

  getAll(): KeyboardShortcut[] {
    return Array.from(this.shortcuts.values());
  }

  clear(): void {
    this.shortcuts.clear();
  }
}

export const keyboardManager = new KeyboardManager();

// Setup default shortcuts
export function setupDefaultShortcuts(handlers: {
  togglePlay: () => void;
  next: () => void;
  previous: () => void;
  volumeUp: () => void;
  volumeDown: () => void;
  toggleMute: () => void;
  seekForward: () => void;
  seekBackward: () => void;
  toggleShuffle: () => void;
  toggleRepeat: () => void;
  showQueue: () => void;
  focusSearch: () => void;
  goHome: () => void;
  showHelp: () => void;
}): void {
  keyboardManager.register({
    key: ' ',
    description: 'Play/Pause',
    handler: handlers.togglePlay,
  });

  keyboardManager.register({
    key: 'k',
    description: 'Play/Pause',
    handler: handlers.togglePlay,
  });

  keyboardManager.register({
    key: 'arrowright',
    description: 'Next track',
    handler: handlers.next,
  });

  keyboardManager.register({
    key: 'n',
    description: 'Next track',
    handler: handlers.next,
  });

  keyboardManager.register({
    key: 'arrowleft',
    description: 'Previous track',
    handler: handlers.previous,
  });

  keyboardManager.register({
    key: 'p',
    description: 'Previous track',
    handler: handlers.previous,
  });

  keyboardManager.register({
    key: 'arrowup',
    description: 'Volume up',
    handler: handlers.volumeUp,
  });

  keyboardManager.register({
    key: 'arrowdown',
    description: 'Volume down',
    handler: handlers.volumeDown,
  });

  keyboardManager.register({
    key: 'm',
    description: 'Toggle mute',
    handler: handlers.toggleMute,
  });

  keyboardManager.register({
    key: 'l',
    description: 'Seek forward 10s',
    handler: handlers.seekForward,
  });

  keyboardManager.register({
    key: 'j',
    description: 'Seek backward 10s',
    handler: handlers.seekBackward,
  });

  keyboardManager.register({
    key: 's',
    description: 'Toggle shuffle',
    handler: handlers.toggleShuffle,
  });

  keyboardManager.register({
    key: 'r',
    description: 'Toggle repeat',
    handler: handlers.toggleRepeat,
  });

  keyboardManager.register({
    key: 'q',
    description: 'Show queue',
    handler: handlers.showQueue,
  });

  keyboardManager.register({
    key: '/',
    description: 'Focus search',
    handler: handlers.focusSearch,
  });

  keyboardManager.register({
    key: 'f',
    ctrl: true,
    description: 'Focus search',
    handler: handlers.focusSearch,
  });

  keyboardManager.register({
    key: 'h',
    description: 'Go home',
    handler: handlers.goHome,
  });

  keyboardManager.register({
    key: '?',
    shift: true,
    description: 'Show keyboard shortcuts',
    handler: handlers.showHelp,
  });
}
